import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { type RootState } from "~/app/store";
import { toMapFn } from "~/common/utils/reducers";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { PdpAccordionType } from "~/features/productDetails/redux/productDetailsTypes";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const START_RESOLVING_ACCORDION_PRODUCT = "resolveAccordion: start";
export const FINISH_RESOLVING_ACCORDION_PRODUCT_SUCCESS = "resolveAccordion: finish";
export const FINISH_RESOLVING_ACCORDION_PRODUCT_EMPTY = "resolveAccordion: empty";

export function resolvePDPAccordionProduct(productCode: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(startResolvingAction());

    let state = getState();
    let product = selectProductDescriptor(state, { productCode, priceReference: "" });

    if (!userHasPermission(Permission.AVAILABILITY)) {
      if (product.base.successorProduct) {
        const successorCode = product.base.successorProduct.code;
        dispatch(finishResolvingWithSuccessorAction(successorCode));
        return;
      } else {
        dispatch(finishResolvingWithNoResultAction());
      }
    }

    await dispatch(requestAvailabilityData(productCode));
    state = getState();
    product = selectProductDescriptor(state, { productCode, priceReference: "" });

    // Case 1b, 2, 3b
    if (selectProductIsInStock(product.availability)) {
      if (product.base.successorProduct) {
        const successorCode = product.base.successorProduct.code;
        dispatch(requestPriceData(successorCode, state.pdp.selectedPriceReference));
        dispatch(requestAvailabilityData(successorCode));
        dispatch(finishResolvingWithSuccessorAction(successorCode));
        return;
      }
    }

    if (product.base.successorProduct) {
      const successorCode = product.base.successorProduct.code;
      const successorAvailability = await dispatch(requestAvailabilityData(successorCode));

      // Case 1aa
      if (successorAvailability && selectProductIsInStock(successorAvailability)) {
        dispatch(requestPriceData(successorCode, getState().pdp.selectedPriceReference));
        dispatch(finishResolvingWithSuccessorAction(successorCode));
        return;
      }
    }

    // Case 1aba, 3aba - there's one alternative. Show if in stock
    // Case 1aba - there's more than one alternative. Show the one that is in stock
    if (product.base.alternativeProducts.length > 0) {
      const availabilities: Dict<Promise<AvailabilityInfo | undefined>> = toMapFn(
        product.base.alternativeProducts,
        (product) => product.code,
        (product) => dispatch(requestAvailabilityData(product.code)!),
      );

      while (Object.keys(availabilities).length > 0) {
        const availability = await Promise.race(Object.values(availabilities));
        if (availability) {
          if (selectProductIsInStock(availability)) {
            const alternativeCode = availability.code;
            dispatch(requestPriceData(alternativeCode, getState().pdp.selectedPriceReference));
            dispatch(finishResolvingWithAlternativeAction(alternativeCode));
            return;
          }
          delete availabilities[availability.code];
        }
      }
    }

    // Last chance - if there is a successor product, take it even if it's not in stock
    // No need to request availability again.
    if (product.base.successorProduct) {
      const successorCode = product.base.successorProduct.code;

      dispatch(requestPriceData(successorCode, getState().pdp.selectedPriceReference));
      dispatch(finishResolvingWithSuccessorAction(successorCode));
      return;
    }

    dispatch(finishResolvingWithNoResultAction());
  };
}

function startResolvingAction() {
  return {
    type: START_RESOLVING_ACCORDION_PRODUCT,
    payload: {},
  } as const;
}

function finishResolvingWithSuccessorAction(productCode: string) {
  return {
    type: FINISH_RESOLVING_ACCORDION_PRODUCT_SUCCESS,
    payload: {
      productCode,
      type: PdpAccordionType.SUCCESSOR,
    },
  } as const;
}

function finishResolvingWithAlternativeAction(productCode: string) {
  return {
    type: FINISH_RESOLVING_ACCORDION_PRODUCT_SUCCESS,
    payload: {
      productCode,
      type: PdpAccordionType.ALTERNATIVE,
    },
  } as const;
}

function finishResolvingWithNoResultAction() {
  return {
    type: FINISH_RESOLVING_ACCORDION_PRODUCT_EMPTY,
    payload: {},
  } as const;
}

export type PdpAccordionActions =
  | ReturnType<typeof startResolvingAction>
  | ReturnType<typeof finishResolvingWithSuccessorAction>
  | ReturnType<typeof finishResolvingWithAlternativeAction>
  | ReturnType<typeof finishResolvingWithNoResultAction>;
